import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentsApi } from '../../api/endpoints';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency } from '../../lib/currency';
import type { VaultEntry } from '@motra/shared';

export default function VaultPage() {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';
  const queryClient = useQueryClient();

  const { data: res, isLoading } = useQuery({
    queryKey: ['vault', shop?.id],
    queryFn: () => paymentsApi.getVault(shop!.id),
    enabled: !!shop?.id,
    select: (r) => r.data ?? [],
  });

  const entries: VaultEntry[] = res ?? [];

  const withdraw = useMutation({
    mutationFn: (data: { shopid: string; amount: number; type: string; note?: string }) =>
      paymentsApi.withdraw(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vault'] }),
  });

  const [amount, setAmount] = useState('');
  const [type, setType] = useState('cash');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function handleWithdraw(e: React.FormEvent) {
    e.preventDefault();
    if (!shop?.id) return;
    setError('');
    setSuccess('');

    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setError('Enter a valid amount.');
      return;
    }

    try {
      await withdraw.mutateAsync({
        shopid: shop.id,
        amount: amt,
        type,
        note: note || undefined,
      });
      setAmount('');
      setNote('');
      setSuccess(`Withdrawal of ${formatCurrency(amt, currSym)} recorded.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Withdrawal failed');
    }
  }

  // Running balance
  const totalIn = entries
    .filter((e) => e.amount > 0)
    .reduce((sum, e) => sum + e.amount, 0);
  const totalOut = entries
    .filter((e) => e.amount < 0)
    .reduce((sum, e) => sum + Math.abs(e.amount), 0);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Vault</h1>
      </div>

      {/* Balance summary */}
      <div className="vault-summary">
        <div className="vault-summary__card">
          <span className="vault-summary__label">Total In</span>
          <span className="vault-summary__amount vault-summary__amount--in">
            {formatCurrency(totalIn, currSym)}
          </span>
        </div>
        <div className="vault-summary__card">
          <span className="vault-summary__label">Total Out</span>
          <span className="vault-summary__amount vault-summary__amount--out">
            {formatCurrency(totalOut, currSym)}
          </span>
        </div>
        <div className="vault-summary__card">
          <span className="vault-summary__label">Balance</span>
          <span className="vault-summary__amount">
            {formatCurrency(totalIn - totalOut, currSym)}
          </span>
        </div>
      </div>

      {/* Withdrawal form */}
      <div className="form-card">
        <h3 className="form-section-title">Record Withdrawal</h3>
        <form onSubmit={handleWithdraw}>
          <div className="form-grid form-grid--3col">
            <div className="form-group">
              <label className="form-label">Amount</label>
              <input
                type="number"
                className="form-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                min={0.01}
                step="0.01"
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="ewallet">E-Wallet</option>
                <option value="transfer">Transfer</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Note</label>
              <input
                type="text"
                className="form-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note"
              />
            </div>
          </div>

          {error && <div className="alert alert--error">{error}</div>}
          {success && <div className="alert alert--success">{success}</div>}

          <button type="submit" className="btn btn--primary" disabled={withdraw.isPending}>
            {withdraw.isPending ? 'Processing...' : 'Record Withdrawal'}
          </button>
        </form>
      </div>

      {/* Transaction log */}
      <div className="form-card">
        <h3 className="form-section-title">Transaction Log</h3>
        {isLoading ? (
          <p>Loading...</p>
        ) : entries.length === 0 ? (
          <p className="text-muted">No vault transactions.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('date', 'Date')}</th>
                <th>Title</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    {entry.created_at
                      ? new Date(entry.created_at).toLocaleString()
                      : '—'}
                  </td>
                  <td>{entry.title}</td>
                  <td>{entry.type ?? '—'}</td>
                  <td className={entry.amount < 0 ? 'text-danger' : 'text-success'}>
                    {entry.amount < 0 ? '−' : '+'}
                    {formatCurrency(Math.abs(entry.amount), currSym)}
                  </td>
                  <td>{entry.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
